import React, { useState } from 'react';
import { View, StyleSheet, Text, Platform, ScrollView, RefreshControl } from 'react-native';
import _ from 'lodash';
import { useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import NavBar from 'src/presentation/components/organisms/NavBar';
import EmptyState from 'src/presentation/components/molecules/EmptyState';
import Loader from 'src/presentation/components/atoms/Loader';
import TopicusListItem from 'src/presentation/components/molecules/TopicusListItem';
import Footer from 'src/presentation/components/organisms/Footer';
import { useFooterHeight } from 'src/presentation/hooks/useFooterHeight';
import { usePageFromRoute } from 'src/presentation/hooks/usePageFromRoute';
import { useEventStore } from 'src/domain/stores/useEventStore';
import Translations from 'src/core/utils/Translations';
import type { RootStackParamList } from 'src/presentation/navigation/types/NavigationTypes';
import Fonts from 'src/core/utils/Fonts';
import Dimens from 'src/core/utils/Dimens';
import Colors from 'src/core/utils/Colors';
import type Constants from 'src/core/utils/Constants';
import type { ApiPageLocationItem, ApiPageLocations } from 'src/core/models/api/ApiPage';

function LocationsScreen() {
	const route = useRoute<RouteProp<RootStackParamList, typeof Constants.screenLocations>>();
	const eventStore = useEventStore();
	const [refreshing, setRefreshing] = useState(false);
	const footerHeight = useFooterHeight();

	const { page, isLoading } = usePageFromRoute(route?.params, { type: 'Locations' });

	const locationsPage = page?.type === 'Locations' ? page : null;

	const handleRefresh = async () => {
		setRefreshing(true);
		const uuid = eventStore.activeEventUuid ?? page?.event_uuid ?? '';
		await eventStore.fetchEvent(uuid, true);
		setRefreshing(false);
	};

	const prepareLocationsArray = (locations: ApiPageLocations | null): Record<string, ApiPageLocationItem[]> => {
		if (!locations) return {};
		const mappedArray: Record<string, ApiPageLocationItem[]> = {};
		locations.children.forEach((location) => {
			const timestamp = location.data.date;
			if (!timestamp) return;
			const date = new Date(timestamp * 1000);
			const days = Translations.days;
			const dayOfWeek = days[date.getDay()] ?? Translations.dayUnknown;

			mappedArray[dayOfWeek] ??= [];
			mappedArray[dayOfWeek].push(location);
		});
		return mappedArray;
	};

	const title = locationsPage?.name ?? '';
	const locations = prepareLocationsArray(locationsPage);

	const renderLocationsList = (locs: Record<string, ApiPageLocationItem[]>) => {
		let count = 0;

		return _.map(locs, (locationday, index) => {
			count++;
			const arrayLength = locationday.length - 1;

			const locationitems = locationday.map((location, i: number) => (
				<TopicusListItem
					data={location}
					image={location.data?.page_image}
					key={i}
					noBorderBottom={arrayLength === i}
					title={location.name}
				/>
			));

			return (
				<View key={index}>
					<Text
						style={[
							styles.heading,
							count !== 1 ? styles.headingLine : null,
							{ color: eventStore.activeTheme.heading_text_color },
						]}
					>
						{index}
					</Text>
					<View style={styles.list}>{locationitems}</View>
				</View>
			);
		});
	};

	return (
		<View style={{ flex: 1 }}>
			<NavBar
				backBtn={true}
				bgImage={eventStore.activeTheme.header_bg_image}
				bgcolor={eventStore.activeTheme.header_bg_color}
				color={eventStore.activeTheme.header_text_color}
				editBtn={false}
				statusBarStyle={eventStore.activeTheme.statusbar_color}
				title={title}
			/>
			<ScrollView
				contentContainerStyle={{ flexGrow: 1, paddingBottom: footerHeight }}
				refreshControl={<RefreshControl refreshing={refreshing} onRefresh={handleRefresh} />}
				style={[styles.container, { flex: 1 }]}
			>
				{isLoading && !locationsPage ? (
					<Loader />
				) : Object.keys(locations).length === 0 ? (
					<EmptyState
						icon="location-outline"
						message={Translations.textNoLocationsMessage}
						title={Translations.textNoLocationsTitle}
					/>
				) : (
					renderLocationsList(locations)
				)}
			</ScrollView>
			<Footer
				backgroundColor={eventStore.activeTheme.footer_bg_color}
				backgroundImage={eventStore.activeTheme.footer_bg_image}
				backgroundType={eventStore.activeTheme.footer_bg_type}
			/>
		</View>
	);
}

export default LocationsScreen;

const styles = StyleSheet.create({
	container: {
		backgroundColor: Platform.OS === 'android' ? Colors.white : undefined,
	},
	list: {
		backgroundColor: Colors.white,
		borderBottomWidth: Platform.OS === 'android' ? 0 : 0.5,
		borderTopWidth: Platform.OS === 'android' ? 0 : 0.5,
		borderColor: Colors.overlay15,
	},
	heading: {
		color: Platform.OS === 'android' ? Colors.blackPure : Colors.greyHeader,
		fontSize: Platform.OS === 'android' ? Dimens.fontSize14 : Dimens.fontSize20,
		paddingLeft: Platform.OS === 'android' ? Dimens.padding0 : Dimens.padding20,
		paddingRight: Platform.OS === 'android' ? Dimens.padding16 : Dimens.padding20,
		marginTop: Platform.OS === 'android' ? Dimens.margin20 : Dimens.margin20,
		marginBottom: Platform.OS === 'android' ? Dimens.margin20 : Dimens.margin10,
		fontFamily: Platform.OS === 'android' ? Fonts.montserratMedium : Fonts.montserratMedium,
		marginLeft: Platform.OS === 'android' ? 80 : undefined,
		opacity: Platform.OS === 'android' ? 0.54 : undefined,
	},
	headingLine: {
		paddingLeft: Platform.OS === 'android' ? Dimens.padding0 : undefined,
		paddingRight: Platform.OS === 'android' ? Dimens.padding16 : undefined,
		marginTop: Platform.OS === 'android' ? Dimens.margin0 : undefined,
		marginBottom: Platform.OS === 'android' ? Dimens.margin20 : undefined,
		borderBottomWidth: Platform.OS === 'android' ? 0 : undefined,
		borderColor: Platform.OS === 'android' ? Colors.greyLine : undefined,
		borderTopWidth: Platform.OS === 'android' ? 0.3333333 : undefined,
		marginLeft: Platform.OS === 'android' ? 80 : undefined,
		paddingTop: Platform.OS === 'android' ? Dimens.padding25 : undefined,
	},
});
